import { useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client.js';
import { Reveal } from '../components/Reveal.js';
import { GoldLeafMark } from '../components/CreditBadge.js';
import { GildButton } from '../components/GildButton.js';
import { ForestScrub } from '../components/ForestScrub.js';
import { Glass } from '../components/Glass.js';
import { GoldWordmark } from '../components/GoldWordmark.js';
import { useReducedMotion } from '../lib/useReducedMotion.js';
import { cn } from '../lib/classnames.js';
import { payForPack, PayError, type PayStatus } from '../web3/pay.js';
import { useConnect } from '../web3/useConnect.js';
import { trackProduct } from '../lib/productMetrics.js';

type Pack = { id: string; label: string; priceUsd: number; credits: number; perks: string[] };

const STATUS_TEXT: Record<PayStatus, string> = {
  connecting: 'Reconnecting your wallet…',
  'switching-network': 'Switching your wallet to Base…',
  'checking-balance': 'Checking your USDC balance…',
  'awaiting-approval': 'Approve the payment in your wallet…',
  confirming: 'Confirming on-chain. This takes a few seconds…',
};

/**
 * Buy: leaves via a one-tap USDC payment on Base.
 *
 * The app builds the exact transfer (token, amount, recipient, chain) so the
 * user only approves; we then verify the transaction on-chain and credit
 * leaves. Heavy on reassurance and explicit steps so no one second-guesses it.
 */
export function Buy() {
  const queryClient = useQueryClient();
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me(), staleTime: 30_000 });
  const packsQuery = useQuery({ queryKey: ['packs'], queryFn: () => api.packs() });

  const [active, setActive] = useState<Pack | null>(null);
  const [connectingId, setConnectingId] = useState<string | null>(null);
  const { connect, error: connectError } = useConnect();

  // Immersive-flight overlay zones, driven imperatively from scroll progress.
  const headerRef = useRef<HTMLDivElement>(null);
  const cueRef = useRef<HTMLDivElement>(null);
  const stepsRef = useRef<HTMLDivElement>(null);
  const packsRef = useRef<HTMLDivElement>(null);
  const checkoutRef = useRef<HTMLDivElement>(null);

  const authed = me.data?.authenticated === true;
  const address = me.data?.address ?? null;
  const payment = packsQuery.data?.payment;

  // Tapping a pack while signed out connects the wallet right here, then opens
  // the payment, no detour to the home page.
  const choosePack = (p: Pack): void => {
    if (!payment?.configured) return;
    if (authed) {
      trackProduct('checkout.started');
      setActive(p);
      return;
    }
    setConnectingId(p.id);
    void connect().then((res) => {
      setConnectingId(null);
      if (res.ok && res.holder) setActive(p);
    });
  };
  const packs = (packsQuery.data?.packs ?? []) as Pack[];
  const reduced = useReducedMotion();

  // The checkout dialog + manual-recovery panel are shared verbatim by both the
  // immersive flight and the reduced-motion page, so the payment flow is one
  // code path no matter how the page is presented.
  const payDialog =
    active && address && payment?.treasury ? (
      <PayDialog
        pack={active}
        from={address}
        treasury={payment.treasury}
        onClose={() => setActive(null)}
        onCredited={() => {
          void queryClient.invalidateQueries({ queryKey: ['me'] });
          void queryClient.invalidateQueries({ queryKey: ['packs'] });
        }}
      />
    ) : null;

  const manualRecover =
    authed && payment?.configured ? (
      <ManualRecover
        packs={packs}
        onCredited={() => void queryClient.invalidateQueries({ queryKey: ['me'] })}
      />
    ) : null;

  // ---- Reduced-motion / no-WebGL: the clean, fully-readable editorial page.
  if (reduced) {
    return (
      <div className="flex flex-col gap-12 py-10 md:py-14">
        <Reveal as="header" className="flex flex-col gap-3">
          <span className="kicker">Get leaves</span>
          <h1 className="heading-display">Top up your leaves.</h1>
          <p className="max-w-prose font-display italic text-lede text-ink-soft">
            One leaf is one gilding. Pay with USDC straight from your wallet. No card,
            no account, no middleman. You approve a single payment and your leaves land
            the moment it confirms.
          </p>
        </Reveal>

        <Reveal>
          <ol className="grid grid-cols-1 gap-5 sm:grid-cols-3">
            {STEPS.map(([n, t, d]) => (
              <li key={n} className="card-quiet flex flex-col gap-1.5 !border-t-0 !pt-0">
                <span className="font-display text-h-display italic leading-none text-accent-deep">
                  {n}
                </span>
                <h3 className="heading-section text-[1.0625rem]">{t}</h3>
                <p className="font-display italic text-caption text-ink-soft">{d}</p>
              </li>
            ))}
          </ol>
        </Reveal>

        {packsQuery.isLoading ? (
          <p className="font-display italic text-ink-subtle">Loading packs…</p>
        ) : (
          <section className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {packs.map((p, i) => (
              <Reveal
                as="article"
                key={p.id}
                delay={i * 80}
                className={
                  p.id === 'standard'
                    ? 'card flex flex-col gap-4 ring-1 ring-accent/40'
                    : 'card-quiet flex flex-col gap-4'
                }
              >
                <div className="flex items-baseline justify-between">
                  <h2 className="heading-section">{p.label}</h2>
                  {p.id === 'standard' && <span className="kicker text-accent-deep">Most popular</span>}
                </div>
                <div className="flex items-end gap-2">
                  <span className="heading-hero leading-none text-ink">${p.priceUsd}</span>
                  <span className="pb-1 font-display italic text-caption text-ink-subtle">USDC</span>
                </div>
                <p className="inline-flex items-center gap-2 font-display text-lede italic text-ink-soft">
                  <GoldLeafMark className="h-5 w-5" />
                  {p.credits} leaves
                </p>
                <ul className="flex flex-col gap-1.5">
                  {p.perks.map((perk) => (
                    <li key={perk} className="flex items-start gap-2 text-caption text-ink-soft">
                      <span aria-hidden className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-accent" />
                      {perk}
                    </li>
                  ))}
                </ul>
                <GildButton
                  className="mt-1 w-full"
                  busy={connectingId === p.id}
                  busyLabel="Connecting…"
                  onClick={() => choosePack(p)}
                  disabled={!payment?.configured}
                  showArrow={!!payment?.configured}
                >
                  {!payment?.configured ? 'Coming soon' : `Pay $${p.priceUsd} USDC`}
                </GildButton>
              </Reveal>
            ))}
          </section>
        )}

        {connectError && <p className="font-display italic text-caption text-danger">{connectError}</p>}
        {authed && payment && !payment.configured && (
          <p className="font-display italic text-caption text-ink-subtle">
            USDC checkout is being set up. It’ll be live here shortly.
          </p>
        )}
        {payment?.configured && <PaymentFootnotes treasury={payment.treasury} />}
        {manualRecover}
        {payDialog}
      </div>
    );
  }

  // ---- The immersive flight: a golden-forest fly-through whose four scenes
  // carry the header, the three steps, the tier cards (over the grove), and the
  // checkout reassurance (in the heart), each surfacing and receding with scroll.
  const handleProgress = (p: number): void => {
    // Header: present at the top, lifts away before the first portal.
    const h = headerRef.current;
    if (h) {
      const o = clamp01(1 - smooth(0.08, 0.18, p));
      h.style.opacity = String(o);
      h.style.transform = `translateY(${(-26 * (1 - o)).toFixed(1)}px) scale(${(0.97 + 0.03 * o).toFixed(3)})`;
      h.style.filter = o < 0.99 ? `blur(${((1 - o) * 5).toFixed(2)}px)` : 'none';
    }
    driveZone(cueRef.current, clamp01(1 - smooth(0.02, 0.1, p)), 0);
    driveZone(stepsRef.current, band(p, 0.26, 0.33, 0.42, 0.49), p < 0.4 ? 1 : -1);
    // Packs: a wide, generous hold over the grove so they're easy to read + tap.
    driveZone(packsRef.current, band(p, 0.5, 0.58, 0.72, 0.8), p < 0.66 ? 1 : -1, true);
    // Checkout reassurance: rises in the heart scene and holds to the end.
    driveZone(checkoutRef.current, clamp01(smooth(0.82, 0.92, p)), 0, true);
  };

  return (
    <>
      <ForestScrub
        src="https://api.gilding.app/images/forest/seedance/avenue-scrub.mp4"
        mobileSrc="https://api.gilding.app/images/forest/seedance/avenue-scrub-720.mp4"
        poster="https://api.gilding.app/images/forest/keys/2.png"
        heightVh={580}
        onProgress={handleProgress}
        className="theme-dark z-0 ml-[calc(50%-50vw)] w-screen"
      >
        {/* ── Threshold ── the invitation */}
        <div
          ref={headerRef}
          className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center px-6 text-center will-change-[opacity,transform]"
        >
          <Halo />
          <span className="kicker relative text-[#f3dca0]">Get leaves</span>
          <h1 className="relative heading-hero mt-3 max-w-3xl text-balance text-ink drop-shadow-[0_3px_26px_rgba(0,0,0,0.95)]">
            Walk in for <em className="foil-text font-display italic">gold</em>.
          </h1>
          <p className="relative lede mx-auto mt-6 max-w-md text-ink/95 drop-shadow-[0_2px_16px_rgba(0,0,0,0.98)]">
            One leaf, one gilding. Pay with USDC. No card, no account.
            Scroll the grove to your pack.
          </p>
        </div>

        {/* scroll cue */}
        <div
          ref={cueRef}
          className="pointer-events-none absolute inset-x-0 bottom-10 z-10 flex justify-center will-change-[opacity]"
        >
          <p className="flex items-center gap-2 font-mono text-caption uppercase tracking-[0.25em] text-ink/85 drop-shadow-[0_2px_12px_rgba(0,0,0,0.95)]">
            <ScrollGlyph /> Scroll to walk the grove
          </p>
        </div>

        {/* ── Path ── how it works */}
        <div
          ref={stepsRef}
          style={{ opacity: 0 }}
          className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center px-6 pb-8 pt-20 will-change-[opacity,transform] sm:py-0"
        >
          <StepsPanel />
        </div>

        {/* ── Grove ── the tier cards. On mobile the cards are centred in the
            band BETWEEN the app header and the fixed bottom nav (not the whole
            viewport), so the third card never tucks behind the nav on a phone
            whose browser chrome shrinks the usable height. */}
        <div
          ref={packsRef}
          style={{ opacity: 0 }}
          className="absolute inset-0 z-10 flex items-center justify-center overflow-y-auto px-5 pt-[4.5rem] pb-[calc(5.25rem_+_env(safe-area-inset-bottom))] will-change-[opacity,transform] sm:py-0"
        >
          <div className="w-full max-w-4xl">
            {packsQuery.isLoading ? (
              <p className="text-center font-display italic text-ink/80">Loading packs…</p>
            ) : (
              <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-3 sm:gap-4">
                {packs.map((p) => (
                  <ForestPack
                    key={p.id}
                    pack={p}
                    busy={connectingId === p.id}
                    disabled={!payment?.configured}
                    onChoose={() => choosePack(p)}
                  />
                ))}
              </div>
            )}
            {connectError && (
              <p className="mt-4 text-center font-display italic text-caption text-danger drop-shadow-[0_2px_10px_rgba(0,0,0,0.9)]">
                {connectError}
              </p>
            )}
            {authed && payment && !payment.configured && (
              <p className="mt-4 text-center font-display italic text-caption text-ink/80">
                USDC checkout is being set up. It’ll be live here shortly.
              </p>
            )}
          </div>
        </div>

        {/* ── Heart ── the reassurance + recovery */}
        <div
          ref={checkoutRef}
          style={{ opacity: 0 }}
          className="absolute inset-0 z-10 flex items-center justify-center px-6 will-change-[opacity,transform]"
        >
          <div className="relative flex w-full max-w-md flex-col items-center gap-5 text-center">
            <Halo />
            <p className="relative font-display text-h-display italic leading-[1.05] text-ink drop-shadow-[0_3px_24px_rgba(0,0,0,0.95)]">
              Bring a sketch.
              <br />
              Leave with <span className="foil-text">gold</span>.
            </p>
            {payment?.configured && (
              <Glass interactive={false} className="relative w-full px-5 py-4">
                <p className="font-display text-[0.9rem] italic leading-snug text-ink/90">
                  Settled on <span className="text-[#ffdf9e]">Base</span>. About a $0.01 network fee,
                  and your wallet stays yours. You approve every payment.
                </p>
                <p className="mt-2.5 flex items-center justify-center gap-1.5 border-t border-hairline/40 pt-2.5 font-mono text-[0.66rem] text-ink/60">
                  {short(payment.treasury)} · verified on-chain
                </p>
              </Glass>
            )}
            {manualRecover && <div className="relative w-full text-center">{manualRecover}</div>}

            {/* the page's end: a quiet, gilded colophon sign-off */}
            <div className="relative mt-2 flex flex-col items-center gap-3">
              <span
                aria-hidden
                className="h-px w-24 bg-gradient-to-r from-transparent via-accent/55 to-transparent"
              />
              <GoldWordmark className="h-7 opacity-80" />
            </div>
          </div>
        </div>
      </ForestScrub>

      {payDialog}
    </>
  );
}

const STEPS: ReadonlyArray<readonly [string, string, string]> = [
  ['1', 'Pick a pack', 'How many leaves you want.'],
  ['2', 'Approve once', 'One tap in your wallet, on Base.'],
  ['3', 'Start gilding', 'Leaves land on-chain, instantly.'],
];

/**
 * StepsPanel: the "how it works" beat as a contained illuminated card, an elite
 * glass plate (frosted, gold-rimmed, light-catching) with corner ticks, a
 * fleuron-flanked title, and three stations strung along a single gilded path
 * (the forest's path, abstracted). Station one wears the house leaf mark; the
 * other two get bespoke heraldic icons. The glass gives the copy a sharp, fully
 * legible home over the bright canopy instead of bare text floating on video.
 */
function StepsPanel() {
  const icons = [
    <GoldLeafMark key="l" className="h-[26px] w-[26px]" />,
    <ShieldCheckIcon key="s" />,
    <GleamIcon key="g" />,
  ];
  return (
    <div className="relative w-full max-w-2xl">
      <Glass className="relative overflow-hidden px-5 py-7 sm:px-10 sm:py-11">
        {/* title */}
        <div className="relative mb-6 flex items-center justify-center gap-3 sm:mb-9">
          <span aria-hidden className="h-px w-10 bg-gradient-to-r from-transparent to-accent/70" />
          <span className="font-mono text-[0.72rem] uppercase tracking-[0.36em] text-[#f3dca0]">
            How it works
          </span>
          <span aria-hidden className="h-px w-10 bg-gradient-to-l from-transparent to-accent/70" />
        </div>

        {/* stations: compact rows on mobile, a centred column on a gilded path on desktop */}
        <ol className="relative grid grid-cols-1 gap-5 sm:grid-cols-3 sm:gap-4">
          {/* the path: horizontal on desktop, behind the medallions */}
          <span
            aria-hidden
            className="absolute left-[16.66%] right-[16.66%] top-[1.7rem] hidden h-px bg-gradient-to-r from-accent/0 via-accent/45 to-accent/0 sm:block"
          />
          {STEPS.map(([n, t, d], i) => (
            <li
              key={n}
              className="relative flex items-center gap-4 text-left sm:flex-col sm:items-center sm:gap-3 sm:text-center"
            >
              <Medallion index={['I', 'II', 'III'][i] ?? n}>{icons[i]}</Medallion>
              <div className="flex flex-col gap-0.5 sm:items-center">
                <h3 className="font-display text-[1.18rem] font-semibold leading-tight text-ink">{t}</h3>
                <p className="max-w-[13rem] font-display text-[0.86rem] italic leading-snug text-ink/80">
                  {d}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </Glass>
    </div>
  );
}

/** A minted gilt coin: a light-catching gold rim (conic, so the highlight travels
 *  the edge), an embossed dark face holding the icon, a soft specular, and a
 *  struck seal at the foot carrying the step's roman numeral. */
function Medallion({ index, children }: { index: string; children: ReactNode }) {
  return (
    <span className="relative flex h-[3.7rem] w-[3.7rem] items-center justify-center">
      {/* warm halo */}
      <span aria-hidden className="absolute -inset-2.5 rounded-full bg-[radial-gradient(closest-side,rgba(233,200,115,0.26),transparent)]" />
      {/* coin rim: conic gold so the light rakes around the edge */}
      <span
        aria-hidden
        className="absolute inset-0 rounded-full"
        style={{
          background:
            'conic-gradient(from 218deg,#6e4814,#e9c873 16%,#fff6da 29%,#c2922f 50%,#6e4814 68%,#e9c873 86%,#6e4814)',
          boxShadow: '0 6px 16px -6px rgba(0,0,0,0.75), inset 0 1px 1px rgba(255,255,255,0.55)',
        }}
      />
      {/* embossed dark face + inner gold hairline */}
      <span
        aria-hidden
        className="absolute inset-[3px] rounded-full"
        style={{
          background: 'radial-gradient(125% 125% at 38% 26%,#1b1626,#0a0810 72%)',
          boxShadow: 'inset 0 0 0 1px rgba(233,200,115,0.22), inset 0 2px 7px rgba(0,0,0,0.72)',
        }}
      />
      {/* polished specular */}
      <span
        aria-hidden
        className="absolute left-[24%] top-[18%] h-2 w-3.5 rounded-full"
        style={{ background: 'radial-gradient(closest-side,rgba(255,255,255,0.55),transparent)', filter: 'blur(1px)' }}
      />
      <span className="relative text-[#f3dca0]">{children}</span>
      {/* struck numeral seal */}
      <span
        className="absolute -bottom-1.5 left-1/2 flex h-[1.3rem] min-w-[1.3rem] -translate-x-1/2 items-center justify-center rounded-full px-1.5"
        style={{
          background: 'linear-gradient(180deg,#fff6da,#e9c873 42%,#bd8c2c)',
          boxShadow: '0 2px 7px -1px rgba(0,0,0,0.62), inset 0 1px 0 rgba(255,255,255,0.7)',
        }}
      >
        <span className="font-display text-[0.74rem] font-semibold not-italic leading-none tracking-tight text-[#3a2708]">
          {index}
        </span>
      </span>
    </span>
  );
}

const ICON = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  width: 22,
  height: 22,
};

/** Approve once: a heraldic shield with a sealed check for one secure, on-chain
 *  approval. A crest reads "trusted + sealed" far better than a wallet glyph. */
function ShieldCheckIcon() {
  return (
    <svg {...ICON}>
      <path d="M12 3.1 5.4 5.5v5.2c0 4 2.7 6.8 6.6 8.1 3.9-1.3 6.6-4.1 6.6-8.1V5.5L12 3.1Z" />
      <path d="M9.1 11.1 11.2 13.3 15 9.3" strokeWidth="1.8" />
    </svg>
  );
}

/** Start gilding: the house "struck gold" gleam, a four-point spark with a
 *  companion glint. The moment a sketch becomes gold. Distinct from the leaf. */
function GleamIcon() {
  return (
    <svg {...ICON}>
      <path
        d="M12 2.7c.56 4.5 2.3 6.24 6.8 6.8-4.5.56-6.24 2.3-6.8 6.8-.56-4.5-2.3-6.24-6.8-6.8 4.5-.56 6.24-2.3 6.8-6.8Z"
        fill="currentColor"
        stroke="none"
      />
      <path
        d="M18.4 14.1c.22 1.55.8 2.13 2.35 2.35-1.55.22-2.13.8-2.35 2.35-.22-1.55-.8-2.13-2.35-2.35 1.55-.22 2.13-.8 2.35-2.35Z"
        fill="currentColor"
        stroke="none"
        opacity="0.85"
      />
    </svg>
  );
}

/** A compact, premium tier card for the immersive grove: gilt plate over the
 *  canopy, the featured pack lifted with a warm ring. Interactive when its scene
 *  is live (the parent zone toggles pointer-events). */
function ForestPack({
  pack,
  busy,
  disabled,
  onChoose,
}: {
  pack: Pack;
  busy: boolean;
  disabled: boolean;
  onChoose: () => void;
}) {
  const featured = pack.id === 'standard';
  return (
    <Glass
      as="article"
      featured={featured}
      className={cn('flex flex-col gap-1 p-3 sm:gap-2.5 sm:p-5', featured && 'sm:-mt-3')}
    >
      <div className="flex items-baseline justify-between">
        <h2 className="font-display text-[1rem] font-semibold text-ink sm:text-[1.0625rem]">{pack.label}</h2>
        {featured && <span className="kicker text-[#ffdf9e]">Popular</span>}
      </div>
      <div className="flex items-end gap-1.5">
        <span className="font-display text-[1.5rem] leading-none text-ink sm:text-h-display">
          ${pack.priceUsd}
        </span>
        <span className="pb-0.5 font-display italic text-caption text-ink/65">USDC</span>
      </div>
      <p className="inline-flex items-center gap-1.5 font-display italic text-[0.85rem] text-ink/90 sm:text-base">
        <GoldLeafMark className="h-4 w-4" />
        {pack.credits} leaves
      </p>
      <ul className="flex flex-col gap-0.5 sm:gap-1">
        {/* "N gildings" repeats the leaf count above. Drop it, keep the substance. */}
        {pack.perks
          .filter((perk) => !/gildings?\b/i.test(perk))
          .map((perk) => (
          <li key={perk} className="flex items-start gap-1.5 text-[0.72rem] leading-snug text-ink/75 sm:text-[0.78rem]">
            <span aria-hidden className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-accent" />
            {perk}
          </li>
        ))}
      </ul>
      <GildButton
        className="mt-0.5 w-full"
        busy={busy}
        busyLabel="Connecting…"
        onClick={onChoose}
        disabled={disabled}
        showArrow={!disabled}
      >
        {disabled ? 'Coming soon' : `Pay $${pack.priceUsd}`}
      </GildButton>
    </Glass>
  );
}

/** Shared reassurance footnotes for the reduced-motion page. */
function PaymentFootnotes({ treasury }: { treasury: string | null }) {
  return (
    <Reveal as="div" className="flex flex-col gap-1 text-caption text-ink-subtle">
      <p className="font-display italic">
        Payments are on <span className="text-ink-soft">Base</span> (network fee ~$0.01). We never
        touch your wallet. You approve each payment yourself.
      </p>
      <p className="font-mono text-[0.6875rem]">
        Receiving wallet: {short(treasury)} · verified on-chain before leaves are added.
      </p>
    </Reveal>
  );
}

/** A soft dark radial behind overlaid type, so copy reads over the bright canopy. */
function Halo({ wide }: { wide?: boolean }) {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
      style={{
        width: wide ? '165%' : '170%',
        height: wide ? '165%' : '185%',
        background:
          'radial-gradient(closest-side, rgba(0,0,0,0.66), rgba(0,0,0,0.36) 56%, transparent 80%)',
        filter: 'blur(10px)',
      }}
    />
  );
}

/** Slender gilded mouse with a falling wheel-dot: the scroll cue. */
function ScrollGlyph() {
  return (
    <svg
      viewBox="0 0 14 22"
      width="13"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      aria-hidden="true"
    >
      <rect x="1.5" y="1.5" width="11" height="19" rx="5.5" />
      <line x1="7" y1="6" x2="7" y2="9.5" strokeLinecap="round">
        <animate attributeName="y1" values="6;10;6" dur="1.8s" repeatCount="indefinite" />
        <animate attributeName="y2" values="9.5;13.5;9.5" dur="1.8s" repeatCount="indefinite" />
      </line>
    </svg>
  );
}


/* ---- scroll-overlay drivers (mirrors the corridor's envelope helpers) ---- */

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
function smooth(edge0: number, edge1: number, x: number): number {
  const t = clamp01((x - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}
/** Rise-in, hold, recede envelope over [a,b]=in, [c,d]=out. */
function band(p: number, a: number, b: number, c: number, d: number): number {
  if (p <= a || p >= d) return 0;
  if (p < b) return smooth(a, b, p);
  if (p > c) return 1 - smooth(c, d, p);
  return 1;
}
/** Drive an overlay zone's opacity/drift/interactivity from a 0→1 envelope. */
function driveZone(el: HTMLDivElement | null, o: number, dir: number, interactive = false): void {
  if (!el) return;
  el.style.opacity = String(o);
  if (dir !== 0) {
    const scale = (0.955 + 0.045 * o).toFixed(3);
    el.style.transform = `translateY(${(18 * (1 - o) * dir).toFixed(1)}px) scale(${scale})`;
    el.style.filter = o < 0.99 ? `blur(${((1 - o) * 6).toFixed(2)}px)` : 'none';
  }
  // Only let a zone capture taps once it's materially visible, so a receded zone
  // never blocks the live one beneath the pointer.
  if (interactive) el.style.pointerEvents = o > 0.5 ? 'auto' : 'none';
}

// ----------------------------------------------------------------------------

type Phase = 'review' | 'paying' | 'verifying' | 'done' | 'error';

function PayDialog({
  pack,
  from,
  treasury,
  onClose,
  onCredited,
}: {
  pack: Pack;
  from: string;
  treasury: string;
  onClose: () => void;
  onCredited: () => void;
}) {
  const [phase, setPhase] = useState<Phase>('review');
  const [status, setStatus] = useState<PayStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [txHash, setTxHash] = useState<string | null>(() => {
    try { const saved = JSON.parse(localStorage.getItem(`gilding_base_order:${from}`) ?? 'null'); return saved?.packId === pack.id && /^0x[0-9a-fA-F]{64}$/.test(saved.signature) ? saved.signature : null; } catch { return null; }
  });
  const [balance, setBalance] = useState<number | null>(null);
  const [lowBalanceWarned, setLowBalanceWarned] = useState(false);

  const run = async (allowLowBalance = false): Promise<void> => {
    setError(null);
    setLowBalanceWarned(false);
    setPhase('paying');
    try {
      const hash = txHash ?? await payForPack({
        priceUsd: pack.priceUsd,
        treasury,
        from,
        onStatus: setStatus,
        allowLowBalance,
      });
      setTxHash(hash);
      try { localStorage.setItem(`gilding_base_order:${from}`, JSON.stringify({ packId: pack.id, signature: hash })); } catch { /* the receipt remains visible in the wallet */ }
      setPhase('verifying');
      const result = await confirmWithRetry(pack.id, hash);
      setBalance(result.balance ?? null);
      setPhase('done');
      localStorage.removeItem(`gilding_base_order:${from}`);
      onCredited();
    } catch (e) {
      if (e instanceof PayError && e.code === 'insufficient_usdc') {
        setLowBalanceWarned(true);
      }
      setError(
        e instanceof PayError
          ? e.message
          : e instanceof Error
            ? e.message
            : 'Something went wrong. Your funds are safe. Try again.',
      );
      setPhase('error');
    }
  };

  const dismissable = phase === 'review' || phase === 'error' || phase === 'done';

  return (
    <div
      className="backdrop-enter fixed inset-0 z-50 flex items-end justify-center bg-ink/50 p-0 md:items-center md:p-6"
      role="dialog"
      aria-modal="true"
      onClick={() => dismissable && onClose()}
    >
      <div
        className="sheet-enter flex w-full max-w-md flex-col gap-5 rounded-t-md bg-surface-elevated p-6 md:rounded-md"
        style={{ boxShadow: 'var(--shadow-lift)', border: '1px solid rgb(var(--color-hairline-strong))' }}
        onClick={(e) => e.stopPropagation()}
      >
        {phase === 'done' ? (
          <>
            <div className="flex flex-col items-center gap-3 py-2 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent/15 text-accent">
                <CheckMark />
              </span>
              <h2 className="heading-section">Leaves added.</h2>
              <p className="inline-flex items-center gap-2 font-display text-lede italic text-ink-soft">
                <GoldLeafMark className="h-5 w-5" />
                {pack.credits} leaves{balance !== null ? ` · balance ${balance}` : ''}
              </p>
              {txHash && <TxLink hash={txHash} />}
            </div>
            <GildButton to="/new" onClick={onClose} className="w-full">
              Start gilding
            </GildButton>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-1">
              <h2 className="heading-section">
                {pack.label} · {pack.credits} leaves
              </h2>
              <p className="font-display italic text-ink-soft">
                You’ll send <span className="not-italic text-ink">${pack.priceUsd} USDC</span> on
                Base. One approval in your wallet. We build the exact amount and address for you,
                so there’s nothing to copy.
              </p>
            </div>

            <dl className="flex flex-col gap-2 rounded-sm border border-hairline/60 bg-surface px-4 py-3 text-caption">
              <Row k="You pay" v={`${pack.priceUsd} USDC`} />
              <Row k="Network" v="Base (fee ~$0.01)" />
              <Row k="To" v={short(treasury)} mono />
              <Row k="You receive" v={`${pack.credits} leaves`} accent />
            </dl>

            {(phase === 'paying' || phase === 'verifying') && (
              <div className="flex items-center gap-3 rounded-sm border border-hairline/60 bg-surface-elevated px-4 py-3">
                <span className="inline-block h-2 w-2 animate-leaf-pulse rounded-full bg-accent" />
                <p className="font-display italic text-caption text-ink-soft">
                  {phase === 'verifying'
                    ? 'Verifying your payment on-chain…'
                    : status
                      ? STATUS_TEXT[status]
                      : 'Starting…'}
                </p>
              </div>
            )}
            {phase === 'paying' || phase === 'verifying' ? (
              <p className="text-center font-display italic text-caption text-ink-subtle">
                Keep this window open until it finishes.
              </p>
            ) : null}

            {error && (
              <p className="font-display italic text-caption text-danger">{error}</p>
            )}
            {txHash && <TxLink hash={txHash} />}

            <div className="flex items-center gap-3">
              {phase !== 'paying' && phase !== 'verifying' && (
                <button type="button" className="btn-ghost" onClick={onClose}>
                  Cancel
                </button>
              )}
              <GildButton
                className="flex-1"
                busy={phase === 'paying' || phase === 'verifying'}
                busyLabel="Working…"
                onClick={() => void run(lowBalanceWarned)}
              >
                {txHash ? 'Confirm previous payment' : phase === 'error'
                  ? lowBalanceWarned
                    ? 'Pay anyway'
                    : 'Try again'
                  : `Pay $${pack.priceUsd} USDC`}
              </GildButton>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ManualRecover({
  packs,
  onCredited,
}: {
  packs: Pack[];
  onCredited: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [packId, setPackId] = useState(packs[0]?.id ?? 'standard');
  const [hash, setHash] = useState('');
  const mut = useMutation({
    mutationFn: () => confirmWithRetry(packId, hash.trim()),
    onSuccess: onCredited,
  });

  if (!open) {
    return (
      <button
        type="button"
        className="self-start font-display text-caption italic text-ink-subtle underline-offset-2 hover:underline"
        onClick={() => setOpen(true)}
      >
        Already paid but didn’t get your leaves?
      </button>
    );
  }
  return (
    <div className="flex flex-col gap-3 rounded-sm border border-hairline/60 bg-surface-elevated p-4">
      <p className="font-display italic text-caption text-ink-soft">
        Paste your payment’s transaction hash and we’ll re-check it on-chain and credit your leaves.
      </p>
      <div className="flex flex-col gap-2 sm:flex-row">
        <select
          value={packId}
          onChange={(e) => setPackId(e.target.value)}
          className="rounded-sm border border-hairline-strong bg-surface px-3 py-2 font-display text-caption text-ink"
        >
          {packs.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label} (${p.priceUsd})
            </option>
          ))}
        </select>
        <input
          value={hash}
          onChange={(e) => setHash(e.target.value)}
          placeholder="0x…"
          className="flex-1 rounded-sm border border-hairline-strong bg-surface px-3 py-2 font-mono text-caption text-ink"
        />
        <button
          type="button"
          className="btn-secondary"
          disabled={!/^0x[0-9a-fA-F]{64}$/.test(hash.trim()) || mut.isPending}
          onClick={() => mut.mutate()}
        >
          {mut.isPending ? 'Checking…' : 'Credit'}
        </button>
      </div>
      {mut.isError && (
        <p className="font-display italic text-caption text-danger">
          {(mut.error)?.message ?? 'Could not verify that transaction.'}
        </p>
      )}
      {mut.isSuccess && (
        <p className="font-display italic text-caption text-accent-deep">
          Verified. {mut.data?.credited ?? 0} leaves added.
        </p>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------

/** Confirm, retrying while the tx is still mining (202 = pending). */
async function confirmWithRetry(
  packId: string,
  txHash: string,
): Promise<{ credited?: number; balance?: number }> {
  for (let attempt = 0; attempt < 8; attempt++) {
    const r = await api.confirmPayment(packId, txHash);
    if (r.ok || r.alreadyCredited) return r;
    if (!r.pending) return r;
    await new Promise((res) => setTimeout(res, 4000));
  }
  throw new Error(
    'Your payment is taking longer than usual to confirm. It will still credit. Check back in a minute, or use the transaction-hash box below.',
  );
}

function Row({ k, v, mono, accent }: { k: string; v: string; mono?: boolean; accent?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-ink-subtle">{k}</dt>
      <dd
        className={
          (mono ? 'font-mono ' : 'font-display ') +
          (accent ? 'text-accent-deep' : 'text-ink')
        }
      >
        {v}
      </dd>
    </div>
  );
}

function TxLink({ hash }: { hash: string }) {
  return (
    <a
      href={`https://basescan.org/tx/${hash}`}
      target="_blank"
      rel="noopener noreferrer"
      className="text-center font-mono text-[0.6875rem] text-ink-subtle underline-offset-2 hover:text-accent-deep hover:underline"
    >
      View transaction ↗
    </a>
  );
}

function CheckMark() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12.5l4.5 4.5L19 7" />
    </svg>
  );
}

function short(addr: string | null): string {
  if (!addr) return '';
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}
